import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { isAfter } from 'date-fns';
import {
  findAvailableSubstituteTeachers,
  createSubstituteAssignment,
  updateSubstituteAssignment,
} from '../../utils/substituteUtils';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabaseClient';
import SnackbarMessage from '../SnackbarMessage';

const SUBSTITUTE_REASONS = [
  'Sick Leave',
  'Personal Leave',
  'Vacation',
  'Emergency',
  'Family Emergency',
  'Other',
];

function SubstituteAssignmentForm({ 
  open, 
  onClose, 
  onSuccess, 
  classId = null, 
  existingAssignment = null,
  prefilledDates = null 
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchingTeachers, setSearchingTeachers] = useState(false);
  const [classes, setClasses] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const [formData, setFormData] = useState({
    classId: classId || '',
    startDate: prefilledDates?.startDate ? dayjs(prefilledDates.startDate) : dayjs(),
    endDate: prefilledDates?.endDate ? dayjs(prefilledDates.endDate) : dayjs(),
    substituteTeacherId: '',
    reason: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  // Load classes and existing assignment data
  useEffect(() => {
    if (open) {
      fetchClasses();
      if (existingAssignment) {
        setFormData({
          classId: existingAssignment.class_id,
          startDate: dayjs(existingAssignment.start_date),
          endDate: dayjs(existingAssignment.end_date),
          substituteTeacherId: existingAssignment.substitute_teacher_id,
          reason: existingAssignment.reason || '',
          notes: existingAssignment.notes || '',
        });
      }
    }
  }, [open, existingAssignment]);

  // Load class details when class is selected
  useEffect(() => {
    if (formData.classId) {
      const classData = classes.find(c => c.id === formData.classId);
      setSelectedClass(classData);
    }
  }, [formData.classId, classes]);

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          id,
          name,
          shift,
          teacher_id,
          teachers:teacher_id (id, first_name, last_name)
        `)
        .order('name');

      if (error) throw error;
      setClasses(data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load classes',
        severity: 'error'
      });
    }
  };

  const searchAvailableTeachers = useCallback(async () => {
    if (!selectedClass || !formData.startDate || !formData.endDate) return;

    setSearchingTeachers(true);
    try {
      const startDateStr = formData.startDate.format('YYYY-MM-DD');
      const endDateStr = formData.endDate.format('YYYY-MM-DD');
      
      const teachers = await findAvailableSubstituteTeachers(
        formData.classId,
        startDateStr,
        endDateStr,
        selectedClass.teacher_id
      );
      
      // If editing and current substitute isn't in available teachers, add them
      let finalTeachers = teachers;
      if (existingAssignment && existingAssignment.substitute_teacher_id) {
        const currentSubstituteExists = teachers.some(t => t.id === existingAssignment.substitute_teacher_id);
        if (!currentSubstituteExists && existingAssignment.substitute_teacher) {
          // Add the currently assigned substitute teacher to the list
          finalTeachers = [existingAssignment.substitute_teacher, ...teachers];
        }
      }
      
      setAvailableTeachers(finalTeachers);
    } catch (error) {
      console.error('Error searching for available teachers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to find available teachers',
        severity: 'error'
      });
    } finally {
      setSearchingTeachers(false);
    }
  }, [selectedClass, formData.startDate, formData.endDate, formData.classId, existingAssignment]);

  // Search for available teachers when class and dates are selected
  useEffect(() => {
    if (formData.classId && formData.startDate && formData.endDate && selectedClass) {
      searchAvailableTeachers();
    }
  }, [formData.classId, formData.startDate, formData.endDate, selectedClass, existingAssignment, searchAvailableTeachers]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.classId) {
      newErrors.classId = 'Class is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Date validation - end date must be >= start date
    if (formData.startDate && formData.endDate && isAfter(formData.startDate.toDate(), formData.endDate.toDate())) {
      newErrors.endDate = 'End date must be on or after start date';
    }

    if (!formData.substituteTeacherId) {
      newErrors.substituteTeacherId = 'Substitute teacher is required';
    }

    // Teacher validation - substitute teacher must be different from original teacher
    if (formData.substituteTeacherId && selectedClass && formData.substituteTeacherId === selectedClass.teacher_id) {
      newErrors.substituteTeacherId = 'Substitute teacher must be different from the original teacher';
    }

    if (!formData.reason) {
      newErrors.reason = 'Reason is required';
    }

    // Status validation (if editing existing assignment)
    if (existingAssignment && !['pending', 'completed', 'cancelled'].includes(formData.status)) {
      newErrors.status = 'Invalid status. Must be pending, completed, or cancelled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const assignmentData = {
        classId: formData.classId,
        originalTeacherId: selectedClass.teacher_id,
        substituteTeacherId: formData.substituteTeacherId,
        startDate: formData.startDate.format('YYYY-MM-DD'),
        endDate: formData.endDate.format('YYYY-MM-DD'),
        reason: formData.reason,
        notes: formData.notes,
        createdBy: user.id,
      };

      let result;
      if (existingAssignment) {
        result = await updateSubstituteAssignment(existingAssignment.id, assignmentData);
      } else {
        result = await createSubstituteAssignment(assignmentData);
      }

      setSnackbar({
        open: true,
        message: `Substitute assignment ${existingAssignment ? 'updated' : 'created'} successfully!`,
        severity: 'success'
      });

      if (onSuccess) onSuccess(result);
      handleClose();
    } catch (error) {
      console.error('Error saving substitute assignment:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${existingAssignment ? 'update' : 'create'} substitute assignment`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      classId: classId || '',
      startDate: dayjs(),
      endDate: dayjs(),
      substituteTeacherId: '',
      reason: '',
      notes: '',
    });
    setErrors({});
    setAvailableTeachers([]);
    setSelectedClass(null);
    onClose();
  };

  const handleDateChange = (field, newDate) => {
    setFormData(prev => ({
      ...prev,
      [field]: newDate
    }));
    
    // Clear substitute teacher selection when dates change
    if (field === 'startDate' || field === 'endDate') {
      setFormData(prev => ({
        ...prev,
        substituteTeacherId: ''
      }));
    }
  };

  const getTeacherDisplayName = (teacher) => {
    if (!teacher) return 'No teacher assigned';
    return `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() || 'Unknown teacher';
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
        }}
      >
        <DialogTitle>
          {existingAssignment ? 'Edit Substitute Assignment' : 'Create Substitute Assignment'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Class Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.classId}>
                <InputLabel>Class</InputLabel>
                <Select
                  value={formData.classId}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    classId: e.target.value,
                    substituteTeacherId: '' // Clear substitute when class changes
                  }))}
                  disabled={!!classId || !!existingAssignment}
                  label="Class"
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      <Box>
                        <Typography variant="body2">
                          {cls.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {cls.shift} • Teacher: {getTeacherDisplayName(cls.teachers)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.classId && <FormHelperText>{errors.classId}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Date Range */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(newDate) => handleDateChange('startDate', newDate)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.startDate,
                      helperText: errors.startDate
                    }
                  }}
                  minDate={dayjs()}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(newDate) => handleDateChange('endDate', newDate)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.endDate,
                      helperText: errors.endDate
                    }
                  }}
                  minDate={formData.startDate || dayjs()}
                />
              </LocalizationProvider>
            </Grid>

            {/* Available Teachers */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Available Substitute Teachers
              </Typography>
              
              {searchingTeachers ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Searching for available teachers...</Typography>
                </Box>
              ) : availableTeachers.length > 0 ? (
                <FormControl fullWidth error={!!errors.substituteTeacherId}>
                  <InputLabel>Select Substitute Teacher</InputLabel>
                  <Select
                    value={formData.substituteTeacherId}
                    onChange={(e) => setFormData(prev => ({ ...prev, substituteTeacherId: e.target.value }))}
                    label="Select Substitute Teacher"
                  >
                    {availableTeachers.map((teacher) => (
                      <MenuItem key={teacher.id} value={teacher.id}>
                        <Box>
                          <Typography variant="body2">
                            {getTeacherDisplayName(teacher)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {teacher.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.substituteTeacherId && <FormHelperText>{errors.substituteTeacherId}</FormHelperText>}
                </FormControl>
              ) : (
                <Alert severity="warning">
                  No teachers are available for the selected class and date range. 
                  This could be because all teachers are already assigned to classes during this shift or have existing substitute assignments.
                </Alert>
              )}
            </Grid>

            {/* Reason */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.reason}>
                <InputLabel>Reason for Substitution</InputLabel>
                <Select
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  label="Reason for Substitution"
                >
                  {SUBSTITUTE_REASONS.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {errors.reason && <FormHelperText>{errors.reason}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes or instructions for the substitute teacher..."
              />
            </Grid>

            {/* Summary */}
            {selectedClass && formData.substituteTeacherId && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Assignment Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Class:</strong> {selectedClass.name} ({selectedClass.shift})
                  </Typography>
                  <Typography variant="body2">
                    <strong>Original Teacher:</strong> {getTeacherDisplayName(selectedClass.teachers)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Substitute Teacher:</strong> {getTeacherDisplayName(
                      availableTeachers.find(t => t.id === formData.substituteTeacherId)
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {formData.startDate.format('MMM D, YYYY')} - {formData.endDate.format('MMM D, YYYY')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Reason:</strong> {formData.reason}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || searchingTeachers || availableTeachers.length === 0}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              existingAssignment ? 'Update Assignment' : 'Create Assignment'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </>
  );
}

export default SubstituteAssignmentForm;
